import { ACTIVITIES, CONTACTS } from 'constants/Entities';

/** Semantic origin for activity details opened from an entity widget. */
export const ACTIVITY_DETAIL_ORIGIN_WIDGET = 'widget';

/**
 * Resolves a display name for the parent entity that owns an Activities widget.
 */
export const getParentEntityDisplayName = (entityType, parentEntity) => {
    if (!parentEntity) return '';

    switch (entityType?.entity) {
        case CONTACTS.entity: {
            const firstName = parentEntity.name || '';
            const surnames = parentEntity.surnames || '';
            return `${firstName} ${surnames}`.trim();
        }
        default:
            return parentEntity.name || '';
    }
};

/**
 * Builds parent context persisted on widget-origin activity detail tabs.
 */
export const buildParentEntityContext = (entityType, entityId, parentEntity) => {
    const entityKey = entityType?.entity;
    if (!entityKey || entityId == null || entityId === '') {
        return null;
    }

    const name = getParentEntityDisplayName(entityType, parentEntity);
    return {
        entity: entityKey,
        id: String(entityId),
        name: name || '',
    };
};

/**
 * Adds the parent entity name to widget-origin activity tab titles.
 */
export const getWidgetActivityTabTitle = (title, parentEntityContext) => {
    if (!parentEntityContext?.name) return title;

    return `${title} - ${parentEntityContext.name}`;
};

/**
 * Builds the `extraId` segment stored on entityDetail actions/state.
 * Activities: `{activityType}` or `{activityType}-{parentEntity}-{parentId}`.
 * Other entities: `origin` when provided.
 */
export const buildEntityDetailExtraId = ({ activityType, origin, parentEntityContext } = {}) => {
    const hasType = activityType != null && activityType !== '';
    const hasParent =
        parentEntityContext?.entity &&
        parentEntityContext?.id != null &&
        parentEntityContext.id !== '';
    if (hasType && hasParent) {
        return `${activityType}-${parentEntityContext.entity}-${parentEntityContext.id}`;
    }
    if (hasType) return String(activityType);
    if (origin) return String(origin);
    return null;
};

/**
 * Builds the Redux entityDetail tab key.
 * Activities: `activities-{id}-{activityType}[-{parentEntity}-{parentId}]`
 * Other entities: `{entity}-{id}` (optional `-{origin}` when provided)
 */
export const buildEntityDetailTabKey = (
    entityName,
    id,
    { activityType, origin, parentEntityContext } = {},
) => {
    if (entityName == null || entityName === '') return '';
    if (id == null || id === '') return String(entityName);

    let key = `${entityName}-${id}`;
    const extraId = buildEntityDetailExtraId({
        activityType: entityName === ACTIVITIES.entity ? activityType : undefined,
        origin,
        parentEntityContext: entityName === ACTIVITIES.entity ? parentEntityContext : undefined,
    });
    if (extraId) key += `-${extraId}`;
    return key;
};

/**
 * Parses a Redux entityDetail tab key into segments.
 */
export const parseEntityDetailTabKey = (tabKey) => {
    if (!tabKey) {
        return {
            entity: null,
            id: null,
            activityType: null,
            origin: null,
            parentEntity: null,
            parentId: null,
            extraId: null,
        };
    }

    const parts = String(tabKey).split('-');
    const entity = parts[0] || null;
    const id = parts[1] || null;

    if (entity === ACTIVITIES.entity) {
        const activityType = parts[2] || null;
        const parentEntity = parts[3] || null;
        const parentId = parts.length > 4 ? parts.slice(4).join('-') : null;
        const hasParent = Boolean(parentEntity && parentId);
        const origin = hasParent ? ACTIVITY_DETAIL_ORIGIN_WIDGET : null;
        const parentEntityContext = hasParent ? { entity: parentEntity, id: parentId } : null;
        const extraId = buildEntityDetailExtraId({ activityType, parentEntityContext });
        return { entity, id, activityType, origin, parentEntity, parentId, extraId };
    }

    const extraId = parts.length > 2 ? parts.slice(2).join('-') : null;
    return {
        entity,
        id,
        activityType: null,
        origin: extraId,
        parentEntity: null,
        parentId: null,
        extraId,
    };
};

/**
 * True when any open tab matches this entity/id (and activityType when provided).
 * Ignores origin so list rows highlight for both list and widget tabs.
 */
export const isEntityDetailTabOpen = (tabs, entityName, id, { activityType } = {}) => {
    if (!tabs || id == null) return false;
    const baseKey = buildEntityDetailTabKey(entityName, id, { activityType });
    return Object.keys(tabs).some((key) => key === baseKey || key.startsWith(`${baseKey}-`));
};

/**
 * Finds an open detail key by entity/id/type, preferring the active detail.
 */
export const findEntityDetailTabKey = (entityDetail, entityName, id, { activityType } = {}) => {
    if (!entityDetail || id == null) return null;

    const matches = (key) => {
        const parsed = parseEntityDetailTabKey(key);
        return (
            parsed.entity === entityName &&
            String(parsed.id) === String(id) &&
            (activityType == null || String(parsed.activityType) === String(activityType))
        );
    };

    if (entityDetail.active && matches(entityDetail.active)) return entityDetail.active;
    return Object.keys(entityDetail.tabs || {}).find(matches) || null;
};

/**
 * Resolves extraId for updates targeting entity+id, preferring the active detail.
 */
export const resolveEntityDetailExtraId = (entityDetail, entityName, id) => {
    if (!entityDetail || id == null) return null;

    const activeKey = entityDetail.active;
    const activeDetail = activeKey ? entityDetail[activeKey] : null;
    if (
        activeDetail?.entity === entityName &&
        String(activeDetail?.id) === String(id) &&
        activeDetail.extraId != null
    ) {
        return activeDetail.extraId;
    }

    const matchingKey = Object.keys(entityDetail.tabs || {}).find((key) => {
        const parsed = parseEntityDetailTabKey(key);
        return parsed.entity === entityName && String(parsed.id) === String(id);
    });

    if (!matchingKey) return null;
    return parseEntityDetailTabKey(matchingKey).extraId;
};
